import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { hasMessage } from '@/lib/message';
import { cn } from '@/lib/utils';
import {
    MutableRefObject,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { LoaderCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
    ChainlitContext,
    FileSpec,
    useChatMessages,
    useChatSession,
    useConfig
} from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import { useTheme } from '@/components/ThemeProvider';

import { useAuth } from '@/hooks/useAuth';

import { pendingPromptState } from '@/state/chat';

import MessageComposer from './MessageComposer';

function StreamingText({ text, className }: { text: string; className?: string }) {
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        if (!text) return;
        // Trigger animation on next frame so CSS transitions apply
        requestAnimationFrame(() => setAnimate(true));
    }, [text]);

    const totalChars = text.length;

    return (
        <span className={className} aria-label={text}>
            {text.split('').map((char, i) => {
                // Accelerating delay: characters reveal faster as the sweep progresses
                const progress = i / totalChars;
                const acceleratedDelay = Math.pow(progress, 0.6) * totalChars * 7;

                return (
                    <span
                        key={i}
                        className="inline-block"
                        style={{
                            opacity: animate ? 1 : 0,
                            transform: animate ? 'translateY(0)' : 'translateY(-1px)',
                            filter: animate ? 'blur(0px) brightness(1)' : 'blur(6px) brightness(1.8)',
                            transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), filter 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            transitionDelay: `${acceleratedDelay}ms`,
                        }}
                    >
                        {char === ' ' ? '\u00A0' : char}
                    </span>
                );
            })}
        </span>
    );
}

interface Props {
    fileSpec: FileSpec;
    onFileUpload: (payload: File[]) => void;
    onFileUploadError: (error: string) => void;
    autoScrollRef: MutableRefObject<boolean>;
}

export default function WelcomeScreen(props: Props) {
    const apiClient = useContext(ChainlitContext);
    const { config } = useConfig();
    const { chatProfile } = useChatSession();
    const { messages } = useChatMessages();
    const { id: urlThreadId } = useParams<{ id?: string }>();
    const { variant } = useTheme();
    const { user } = useAuth();
    const [isVisible, setIsVisible] = useState(false);
    const startingChat = useRecoilValue(pendingPromptState) !== null;
    const displayName =
        (user as any)?.display_name || (user as any)?.identifier || '';
    const firstName = displayName.split(' ')[0];

    const chatProfiles = config?.chatProfiles;

    const allowHtml = config?.features?.unsafe_allow_html;
    const latex = config?.features?.latex;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const logo = useMemo(() => {
        if (chatProfile && chatProfiles) {
            const currentChatProfile = chatProfiles.find(
                (cp) => cp.name === chatProfile
            );
            if (currentChatProfile?.icon) {
                return (
                    <div className="flex flex-col items-center gap-2 mb-2">
                        <img
                            className="rounded-full w-16 h-16"
                            src={
                                currentChatProfile?.icon.startsWith('/public')
                                    ? apiClient.buildEndpoint(
                                          currentChatProfile?.icon
                                      )
                                    : currentChatProfile?.icon
                            }
                        />
                        {currentChatProfile?.markdown_description ? (
                            <Markdown allowHtml={allowHtml} latex={latex}>
                                {currentChatProfile.markdown_description}
                            </Markdown>
                        ) : null}
                    </div>
                );
            }
        }

        // Theme-aware default logo/avatar
        const logoImage =
            variant === 'dark' ? 'DM_Diamond_White.svg' : 'DM_Diamond_Navy.svg';
        return (
            <div className="flex flex-col items-center gap-2 mb-2">
                <img
                    className="w-[200px] h-[200px] object-contain"
                    src={apiClient.buildEndpoint(`/public/${logoImage}`)}
                    alt="Logo"
                />
            </div>
        );
    }, [chatProfiles, chatProfile, variant, apiClient, allowHtml, latex]);

    if (hasMessage(messages) || urlThreadId) return null;

    return (
        <div
            id="welcome-screen"
            className={cn(
                'relative flex flex-col items-center mx-auto w-full font-regular welcome-screen',
                isEmbeddedSidebar
                    ? 'opacity-100 grow justify-center gap-6 px-0.5 max-w-full'
                    : cn(
                        'opacity-0 transition-opacity duration-500 delay-100',
                        isVisible && 'opacity-100',
                        'grow justify-center gap-12 max-w-3xl'
                    )
            )}
        >
            {startingChat && (
                <div className="absolute inset-0 flex justify-center items-center gap-2 text-muted-foreground">
                    <LoaderCircle className="size-5 animate-spin" />
                    Starting chat...
                </div>
            )}
            <h1
                className={cn(
                    'text-center',
                    startingChat && 'invisible',
                    isEmbeddedSidebar
                        ? cn('text-lg', firstName ? 'visible' : 'invisible')
                        : cn(
                            'text-4xl transition-all duration-700 ease-out',
                            isVisible
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-8'
                        )
                )}
            >
                {isEmbeddedSidebar && firstName ? (
                    <StreamingText text={`Hi ${firstName}, how can I help?`} />
                ) : (
                    <>Hi {firstName}{firstName ? ', ' : ''}how can I help?</>
                )}
            </h1>
            <div className={cn('w-full', startingChat && 'invisible')}>
                <MessageComposer {...props} />
            </div>
            {/* <Starters /> */}
        </div>
    );
}
